/**
 * Reading and re-checking a workspace block (403 TENANT_SUSPENDED /
 * TENANT_NOT_ACTIVE from the session gate, recorded by the API client).
 */
import type { QueryClient } from "@tanstack/react-query";
import { get } from "@/lib/api/client";
import { useAuthStore, type WorkspaceBlock } from "@/lib/auth/store";

export type WorkspaceBlockKind =
  | "suspended"
  | "provisioning"
  | "legalHold"
  | "closed"
  | "notFound"
  | "notActive";

/**
 * Which situation the block describes. TENANT_NOT_ACTIVE covers a workspace
 * its owner created a minute ago (Provisioning) as well as a closed one, and
 * the API names the status only in `detail` — "This workspace is not active
 * (status: Provisioning)." — so the copy is chosen by it, as the error catalog
 * does. Anything unrecognised gets the general wording, never a wrong one.
 */
export function workspaceBlockKind(block: WorkspaceBlock): WorkspaceBlockKind {
  if (block.code === "TENANT_SUSPENDED") return "suspended";
  const status = /\(status:\s*([A-Za-z]+)\)/.exec(block.detail)?.[1];
  switch (status) {
    case "Provisioning":
      return "provisioning";
    case "Suspended":
      return "suspended";
    case "LegalHold":
      return "legalHold";
    case "Closed":
      return "closed";
  }
  if (/does not exist/i.test(block.detail)) return "notFound";
  return "notActive";
}

/**
 * "Check again": ask the server whether the workspace is usable now, through
 * a route the session gate guards.
 *
 * - Usable: every cached query is reset (so the screens that come back load
 *   fresh, not the errors that raised the block) and the block is cleared.
 * - Still refused: the client has already recorded the new answer (the status
 *   may have changed, e.g. Provisioning to Suspended); resolves false.
 * - A 401 ends the session through the client as usual; resolves false.
 */
export async function checkWorkspaceAgain(
  queryClient: QueryClient,
): Promise<boolean> {
  try {
    await get<unknown>("/users/me");
  } catch {
    return false;
  }
  await queryClient.resetQueries();
  useAuthStore.getState().setWorkspaceBlock(null);
  return true;
}
